/**
 * Contributions Page
 * Record and manage tithes, offerings, and donations
 */

import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Plus,
  Search,
  Filter,
  Download,
  Calendar as CalendarIcon,
  DollarSign,
  User,
  FileText,
  Layers,
  Zap,
  Check,
  ChevronsUpDown,
  X
} from 'lucide-react'
import { AdminUser } from '@/lib/authService'
import { ContributionService, type Contribution } from '@/services/contributionService'
import { digitalPaymentService, type DigitalPaymentWithMember } from '@/services/digitalPaymentService'
import { NotificationService } from '@/services/notificationService'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { formatCurrency, getSupportedCurrencies } from '@/lib/currency'
import { cn } from '@/lib/utils'

interface ContributionsProps {
  user: AdminUser | null
}

// Member interface for search
interface Member {
  id: string
  preferred_name: string
  email?: string
  phone_number?: string
  member_number?: string
}

// Bulk entry row interface
interface BulkEntryRow {
  id: string
  member_id: string
  member_name: string
  contribution_type: string
  amount: string
  currency: string
}

export default function Contributions({ user }: ContributionsProps) {
  const [contributions, setContributions] = useState<Contribution[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterMethod, setFilterMethod] = useState<string>('all')

  // Member search state
  const [members, setMembers] = useState<Member[]>([])
  const [memberSearchOpen, setMemberSearchOpen] = useState(false)
  const [memberSearchValue, setMemberSearchValue] = useState('')

  // Bulk entry state
  const [showBulkDialog, setShowBulkDialog] = useState(false)
  const [bulkEntries, setBulkEntries] = useState<BulkEntryRow[]>([
    { id: '1', member_id: '', member_name: '', contribution_type: 'tithe', amount: '', currency: 'GHS' }
  ])

  // Digital payments state
  const [digitalPayments, setDigitalPayments] = useState<DigitalPaymentWithMember[]>([])

  // Form state
  const [formData, setFormData] = useState({
    member_id: '',
    contributor_name: '',
    contributor_email: '',
    contributor_phone: '',
    contribution_date: format(new Date(), 'yyyy-MM-dd'),
    contribution_type: 'tithe',
    contribution_method: 'cash',
    amount: '',
    currency: 'GHS',
    designation: '',
    reference_number: '',
    is_tax_deductible: true,
    notes: ''
  })

  useEffect(() => {
    if (user?.churchId) {
      loadContributions()
      loadMembers()
      // TODO: Enable when digital_payments table is created
      // loadDigitalPayments()
    }
  }, [user, filterType, filterMethod])

  async function loadContributions() {
    try {
      setLoading(true)
      const result = await ContributionService.getContributions(user!.churchId, {
        contributionType: filterType !== 'all' ? filterType : undefined,
        status: 'completed',
        limit: 50
      })
      setContributions(result.data)
    } catch (error) {
      console.error('Error loading contributions:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadMembers() {
    try {
      const { data, error } = await supabase
        .from('members')
        .select('id, preferred_name, email, phone_number, member_number')
        .eq('church_id', user!.churchId)
        .eq('status', 'active')
        .order('preferred_name')

      if (error) throw error
      setMembers(data || [])
    } catch (error) {
      console.error('Error loading members:', error)
    }
  }

  async function loadDigitalPayments() {
    try {
      const payments = await digitalPaymentService.getChurchPayments(user!.churchId, {
        status: 'completed'
      })
      // Filter out payments that are already recorded in ledger
      const unrecordedPayments = payments.filter(p => !p.recorded_in_ledger)
      setDigitalPayments(unrecordedPayments)
    } catch (error) {
      console.error('Error loading digital payments:', error)
      // Set empty array on error to prevent infinite loops
      setDigitalPayments([])
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      // If church is the contributor, set member_id to null
      const submissionData = {
        church_id: user!.churchId,
        member_id: formData.member_id === 'church' ? null : formData.member_id || null,
        contributor_name: formData.contributor_name,
        contributor_email: formData.contributor_email || null,
        contributor_phone: formData.contributor_phone || null,
        contribution_date: formData.contribution_date,
        contribution_type: formData.contribution_type,
        contribution_method: formData.contribution_method,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        designation: formData.designation || null,
        reference_number: formData.reference_number || null,
        is_tax_deductible: formData.is_tax_deductible,
        notes: formData.notes || null,
        status: 'completed'
      }

      console.log('Submitting contribution:', submissionData)
      const result = await ContributionService.createContribution(submissionData, user!.id)
      console.log('Contribution created:', result)

      // Send notification to member if applicable
      if (formData.member_id && formData.member_id !== 'church') {
        const notificationResult = await NotificationService.notifyContributionRecorded(
          user!.churchId,
          formData.member_id,
          {
            amount: parseFloat(formData.amount),
            currency: formData.currency,
            contribution_type: formData.contribution_type,
            contribution_date: formData.contribution_date,
            reference_number: formData.reference_number || undefined
          }
        )

        if (!notificationResult.success && notificationResult.error !== 'mnotify not configured') {
          console.warn('Failed to send notification:', notificationResult.error)
          // Don't fail the contribution if notification fails
        }
      }

      setShowAddDialog(false)
      loadContributions()
      resetForm()
      alert('Contribution recorded successfully!')
    } catch (error: any) {
      console.error('Error creating contribution:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))

      // Check for RLS policy error
      if (error.code === '42501' || error.message?.includes('row-level security')) {
        alert(
          'Database Permission Error\n\n' +
          'The contributions table needs Row Level Security policies configured.\n\n' +
          'Please ask your database administrator to:\n' +
          '1. Go to Supabase Dashboard → Table Editor → contributions\n' +
          '2. Click "RLS disabled" to enable RLS\n' +
          '3. Add an INSERT policy that allows authenticated users to create contributions\n\n' +
          'Example policy:\n' +
          'CREATE POLICY "Allow authenticated users to insert contributions"\n' +
          'ON contributions FOR INSERT\n' +
          'TO authenticated\n' +
          'WITH CHECK (true);'
        )
        return
      }

      // More detailed error message for other errors
      let errorMessage = 'Failed to record contribution'
      if (error.message) {
        errorMessage += `: ${error.message}`
      }
      if (error.details) {
        errorMessage += `\nDetails: ${error.details}`
      }
      if (error.hint) {
        errorMessage += `\nHint: ${error.hint}`
      }

      alert(errorMessage)
    }
  }

  function resetForm() {
    setFormData({
      member_id: '',
      contributor_name: '',
      contributor_email: '',
      contributor_phone: '',
      contribution_date: format(new Date(), 'yyyy-MM-dd'),
      contribution_type: 'tithe',
      contribution_method: 'cash',
      amount: '',
      currency: 'GHS',
      designation: '',
      reference_number: '',
      is_tax_deductible: true,
      notes: ''
    })
    setMemberSearchValue('')
  }

  // Member search handler
  function handleMemberSelect(memberId: string) {
    const member = members.find(m => m.id === memberId)
    if (member) {
      setFormData({
        ...formData,
        member_id: member.id,
        contributor_name: member.preferred_name,
        contributor_email: member.email || '',
        contributor_phone: member.phone_number || ''
      })
      setMemberSearchValue(member.preferred_name)
      setMemberSearchOpen(false)
    }
  }

  // Bulk entry handlers
  function addBulkRow() {
    const newId = (bulkEntries.length + 1).toString()
    setBulkEntries([
      ...bulkEntries,
      { id: newId, member_id: '', member_name: '', contribution_type: 'tithe', amount: '', currency: 'GHS' }
    ])
  }

  function removeBulkRow(id: string) {
    if (bulkEntries.length > 1) {
      setBulkEntries(bulkEntries.filter(row => row.id !== id))
    }
  }

  function updateBulkRow(id: string, field: keyof BulkEntryRow, value: string) {
    setBulkEntries(bulkEntries.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ))
  }

  function selectBulkMember(rowId: string, memberId: string) {
    const member = members.find(m => m.id === memberId)
    if (member) {
      setBulkEntries(bulkEntries.map(row =>
        row.id === rowId
          ? { ...row, member_id: member.id, member_name: member.preferred_name }
          : row
      ))
    }
  }

  async function handleBulkSubmit() {
    try {
      const validEntries = bulkEntries.filter(e => e.amount && parseFloat(e.amount) > 0)

      if (validEntries.length === 0) {
        alert('Please add at least one contribution with an amount')
        return
      }

      for (const entry of validEntries) {
        const member = members.find(m => m.id === entry.member_id)
        await ContributionService.createContribution(
          {
            church_id: user!.churchId,
            member_id: entry.member_id || undefined,
            contributor_name: entry.member_name || 'Anonymous',
            contributor_email: member?.email,
            contributor_phone: member?.phone_number,
            contribution_date: format(new Date(), 'yyyy-MM-dd'),
            contribution_type: entry.contribution_type,
            contribution_method: 'cash',
            amount: parseFloat(entry.amount),
            currency: entry.currency,
            is_tax_deductible: true,
            notes: 'Bulk entry'
          },
          user!.id
        )
      }

      setShowBulkDialog(false)
      setBulkEntries([
        { id: '1', member_id: '', member_name: '', contribution_type: 'tithe', amount: '', currency: 'GHS' }
      ])
      loadContributions()
      alert(`Successfully recorded ${validEntries.length} contributions`)
    } catch (error) {
      console.error('Error creating bulk contributions:', error)
      alert('Failed to record some contributions')
    }
  }

  // Digital payment handler
  async function recordDigitalPayment(paymentId: string) {
    try {
      const result = await digitalPaymentService.recordPaymentInLedger(paymentId)
      if (result.success) {
        loadDigitalPayments()
        loadContributions()
        alert('Payment recorded successfully')
      } else {
        alert(`Failed to record payment: ${result.error}`)
      }
    } catch (error) {
      console.error('Error recording digital payment:', error)
      alert('Failed to record payment')
    }
  }

  // Reverse contribution handler
  async function handleReverseContribution(contributionId: string) {
    const reason = prompt('Please provide a reason for reversing this contribution:')
    if (!reason || reason.trim() === '') {
      alert('Reversal cancelled - reason is required')
      return
    }

    const confirmed = confirm(
      'Are you sure you want to reverse this contribution?\n\n' +
      'This will:\n' +
      '1. Mark the contribution as VOIDED\n' +
      '2. Automatically create reversing journal entries\n' +
      '3. Update account balances to reverse the transaction\n' +
      '4. Maintain a complete audit trail\n\n' +
      'This action cannot be undone.'
    )

    if (!confirmed) return

    try {
      await ContributionService.reverseContribution(contributionId, user!.id, reason)
      loadContributions()
      alert('Contribution reversed successfully')
    } catch (error: any) {
      console.error('Error reversing contribution:', error)
      alert(`Failed to reverse contribution: ${error.message}`)
    }
  }

  const filteredContributions = contributions.filter(c => {
    const matchesSearch =
      c.contributor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesMethod = filterMethod === 'all' || c.contribution_method === filterMethod

    return matchesSearch && matchesMethod
  })

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Contributions</h1>
            <p className="text-sm text-muted-foreground">
              Record and manage tithes, offerings, and donations
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBulkDialog(true)}>
              <Layers className="mr-2 h-4 w-4" />
              Bulk Entry
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Single Entry
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="tithe">Tithe</SelectItem>
              <SelectItem value="offering">Offering</SelectItem>
              <SelectItem value="donation">Donation</SelectItem>
              <SelectItem value="pledge_payment">Pledge Payment</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="check">Check</SelectItem>
              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
              <SelectItem value="mobile_payment">Mobile Payment</SelectItem>
              <SelectItem value="credit_card">Credit Card</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-auto p-6">
        <Tabs defaultValue="recorded" className="w-full">
          <TabsList>
            <TabsTrigger value="recorded">
              <FileText className="mr-2 h-4 w-4" />
              Recorded Contributions
            </TabsTrigger>
            <TabsTrigger value="digital">
              <Zap className="mr-2 h-4 w-4" />
              Digital Payments
              {digitalPayments.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {digitalPayments.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Recorded Contributions Tab */}
          <TabsContent value="recorded" className="mt-6">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Contributor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Tax Receipt</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredContributions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No contributions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContributions.map((contribution) => (
                      <TableRow key={contribution.id} className={contribution.status === 'voided' ? 'opacity-50 bg-muted/50' : ''}>
                        <TableCell>
                          {format(new Date(contribution.contribution_date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {contribution.contributor_name || 'Anonymous'}
                              {contribution.status === 'voided' && (
                                <Badge variant="destructive" className="ml-2 text-xs">VOIDED</Badge>
                              )}
                            </div>
                            {contribution.contributor_email && (
                              <div className="text-xs text-muted-foreground">
                                {contribution.contributor_email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {contribution.contribution_type.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="capitalize">
                          {contribution.contribution_method.replace('_', ' ')}
                        </TableCell>
                        <TableCell>
                          {contribution.designation || '-'}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${contribution.amount < 0 ? 'text-destructive' : ''}`}>
                          {formatCurrency(contribution.amount, contribution.currency || 'GHS')}
                        </TableCell>
                        <TableCell>
                          {contribution.is_tax_deductible ? (
                            <Badge variant="secondary">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                            {contribution.status !== 'voided' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleReverseContribution(contribution.id)}
                              >
                                Reverse
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Digital Payments Tab */}
          <TabsContent value="digital" className="mt-6">
            {digitalPayments.length > 0 && (
              <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-4">
                <div className="flex items-start">
                  <Zap className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <h3 className="font-medium text-yellow-900">Unrecorded Digital Payments</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      {digitalPayments.length} payment{digitalPayments.length !== 1 ? 's' : ''} from Paystack need to be recorded in the ledger
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {digitalPayments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No unrecorded digital payments
                      </TableCell>
                    </TableRow>
                  ) : (
                    digitalPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {payment.member?.preferred_name || 'Unknown'}
                            </div>
                            {payment.member?.email && (
                              <div className="text-xs text-muted-foreground">
                                {payment.member.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">
                          {payment.payment_method?.replace('_', ' ') || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{payment.transaction_reference}</code>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(payment.amount, payment.currency || 'GHS')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{payment.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => recordDigitalPayment(payment.id)}
                          >
                            Record
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Contribution Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Contribution</DialogTitle>
            <DialogDescription>
              Add a new contribution, tithe, offering, or donation
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Date */}
              <div className="grid gap-2">
                <Label htmlFor="contribution_date">Date *</Label>
                <Input
                  id="contribution_date"
                  type="date"
                  value={formData.contribution_date}
                  onChange={(e) => setFormData({ ...formData, contribution_date: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Type */}
                <div className="grid gap-2">
                  <Label htmlFor="contribution_type">Type *</Label>
                  <Select
                    value={formData.contribution_type}
                    onValueChange={(value) => setFormData({ ...formData, contribution_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.member_id === 'church' ? (
                        <>
                          <SelectItem value="donation">Donation / Gift</SelectItem>
                          <SelectItem value="pledge_payment">Pledge Payment</SelectItem>
                          <SelectItem value="grant">Grant</SelectItem>
                          <SelectItem value="offering">Mission Support</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="tithe">Tithe</SelectItem>
                          <SelectItem value="offering">Offering</SelectItem>
                          <SelectItem value="donation">Donation</SelectItem>
                          <SelectItem value="pledge_payment">Pledge Payment</SelectItem>
                          <SelectItem value="grant">Grant</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Method */}
                <div className="grid gap-2">
                  <Label htmlFor="contribution_method">Method *</Label>
                  <Select
                    value={formData.contribution_method}
                    onValueChange={(value) => setFormData({ ...formData, contribution_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="mobile_payment">Mobile Payment</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Amount and Currency */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="currency">Currency *</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getSupportedCurrencies().map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contributor Type Selection */}
              <div className="grid gap-2">
                <Label>Who is contributing?</Label>
                <Select
                  value={formData.member_id === 'church' ? 'church' : formData.member_id ? 'member' : 'guest'}
                  onValueChange={(value) => {
                    if (value === 'church') {
                      setFormData({
                        ...formData,
                        member_id: 'church',
                        contributor_name: 'Church',
                        contributor_email: '',
                        contributor_phone: ''
                      })
                      setMemberSearchValue('Church')
                    } else if (value === 'member') {
                      setFormData({
                        ...formData,
                        member_id: '',
                        contributor_name: '',
                        contributor_email: '',
                        contributor_phone: ''
                      })
                      setMemberSearchValue('')
                    } else {
                      setFormData({
                        ...formData,
                        member_id: '',
                        contributor_name: '',
                        contributor_email: '',
                        contributor_phone: ''
                      })
                      setMemberSearchValue('')
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Church Member</SelectItem>
                    <SelectItem value="guest">Guest / Visitor</SelectItem>
                    <SelectItem value="church">The Church (Outgoing)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Member Search - Only show if NOT church */}
              {formData.member_id !== 'church' && (
                <div className="grid gap-2">
                  <Label>Contributor</Label>
                  <Popover open={memberSearchOpen} onOpenChange={setMemberSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={memberSearchOpen}
                        className="w-full justify-between"
                      >
                        {memberSearchValue || "Select member or type name..."}
                        {memberSearchValue && (
                          <X
                            className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation()
                              setFormData({
                                ...formData,
                                member_id: '',
                                contributor_name: '',
                                contributor_email: '',
                                contributor_phone: ''
                              })
                              setMemberSearchValue('')
                            }}
                          />
                        )}
                        {!memberSearchValue && (
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput
                          placeholder="Search members..."
                          value={memberSearchValue}
                          onValueChange={setMemberSearchValue}
                        />
                        <CommandList>
                          <CommandEmpty>
                            <div className="p-2">
                              <p className="text-sm text-muted-foreground mb-2">No member found</p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    member_id: '',
                                    contributor_name: memberSearchValue,
                                    contributor_email: '',
                                    contributor_phone: ''
                                  })
                                  setMemberSearchOpen(false)
                                }}
                              >
                                Use "{memberSearchValue}" as guest
                              </Button>
                            </div>
                          </CommandEmpty>
                          <CommandGroup>
                            {members.map((member) => (
                              <CommandItem
                                key={member.id}
                                value={member.id}
                                onSelect={() => handleMemberSelect(member.id)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.member_id === member.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span>{member.preferred_name}</span>
                                  {member.email && (
                                    <span className="text-xs text-muted-foreground">{member.email}</span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Only show email and phone fields for guests (non-members) */}
              {!formData.member_id && formData.contributor_name && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Email */}
                  <div className="grid gap-2">
                    <Label htmlFor="contributor_email">Email (Optional)</Label>
                    <Input
                      id="contributor_email"
                      type="email"
                      value={formData.contributor_email}
                      onChange={(e) => setFormData({ ...formData, contributor_email: e.target.value })}
                      placeholder="guest@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div className="grid gap-2">
                    <Label htmlFor="contributor_phone">Phone (Optional)</Label>
                    <Input
                      id="contributor_phone"
                      value={formData.contributor_phone}
                      onChange={(e) => setFormData({ ...formData, contributor_phone: e.target.value })}
                      placeholder="+233 XX XXX XXXX"
                    />
                  </div>
                </div>
              )}

              {/* Designation - Different label for church outgoing */}
              <div className="grid gap-2">
                <Label htmlFor="designation">
                  {formData.member_id === 'church' ? 'Recipient / Cause *' : 'Designation / Purpose'}
                </Label>
                <Input
                  id="designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder={
                    formData.member_id === 'church'
                      ? 'e.g., World Missions, Local Charity, Sister Church'
                      : 'e.g., Building Fund, Missions'
                  }
                  required={formData.member_id === 'church'}
                />
              </div>

              {/* Reference Number */}
              <div className="grid gap-2">
                <Label htmlFor="reference_number">Reference Number</Label>
                <Input
                  id="reference_number"
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Check number or transaction ID"
                />
              </div>

              {/* Notes */}
              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Tax Deductible */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_tax_deductible"
                  checked={formData.is_tax_deductible}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_tax_deductible: checked as boolean })
                  }
                />
                <Label htmlFor="is_tax_deductible" className="cursor-pointer">
                  Tax deductible (eligible for tax receipt)
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Record Contribution
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Entry Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Contribution Entry</DialogTitle>
            <DialogDescription>
              Record multiple contributions at once (e.g., during a service)
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4">
              <Button onClick={addBulkRow} size="sm" variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add Row
              </Button>
            </div>

            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Member</TableHead>
                    <TableHead className="w-[150px]">Type</TableHead>
                    <TableHead className="w-[120px]">Amount</TableHead>
                    <TableHead className="w-[100px]">Currency</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bulkEntries.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Select
                          value={row.member_id}
                          onValueChange={(value) => selectBulkMember(row.id, value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select member..." />
                          </SelectTrigger>
                          <SelectContent>
                            {members.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.preferred_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={row.contribution_type}
                          onValueChange={(value) => updateBulkRow(row.id, 'contribution_type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tithe">Tithe</SelectItem>
                            <SelectItem value="offering">Offering</SelectItem>
                            <SelectItem value="donation">Donation</SelectItem>
                            <SelectItem value="pledge_payment">Pledge Payment</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={row.amount}
                          onChange={(e) => updateBulkRow(row.id, 'amount', e.target.value)}
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={row.currency}
                          onValueChange={(value) => updateBulkRow(row.id, 'currency', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {getSupportedCurrencies().map((currency) => (
                              <SelectItem key={currency.code} value={currency.code}>
                                {currency.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBulkRow(row.id)}
                          disabled={bulkEntries.length === 1}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 text-sm text-muted-foreground">
              Total entries: {bulkEntries.filter(e => e.amount && parseFloat(e.amount) > 0).length} |
              Total amount: {formatCurrency(
                bulkEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
                'GHS'
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowBulkDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkSubmit}>
              Record All Contributions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
